import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { MapPin, Phone, ArrowLeft, Eye, X } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BACKEND = process.env.REACT_APP_BACKEND_URL;

const STATUS_BG = { "متاح": "bg-green-500", "محجوز": "bg-amber-500", "مباع": "bg-red-500" };

const DEFAULT_PC = {
  preview_badge:    "مخططات سكنية",
  preview_title:    "مخططات جازان",
  preview_subtitle: "قطع أراضي مميزة في منطقة جازان بمواقع استراتيجية وأسعار تنافسية",
  preview_btn:      "عرض جميع المخططات",
  price_label:      "متوسط سعر المتر",
  total_plots_label:"إجمالي القطع",
  area_label:       "المساحة",
  contact_btn:      "تواصل معنا",
  view_pdf_btn:     "مشاهدة المخطط",
};

const fullUrl = (url) => (!url ? null : url.startsWith("/api/") ? `${BACKEND}${url}` : url);

const PlotsPreview = ({ contact }) => {
  const [plots, setPlots]       = useState([]);
  const [pageContent, setPc]    = useState({});
  const [pdfModal, setPdfModal] = useState({ open: false, url: "", title: "" });

  const pc = { ...DEFAULT_PC, ...pageContent };
  const openPdf  = (url, title) => setPdfModal({ open: true, url, title });
  const closePdf = () => setPdfModal({ open: false, url: "", title: "" });

  useEffect(() => {
    Promise.all([
      axios.get(`${API}/plots`),
      axios.get(`${API}/content/plots_page`),
    ])
      .then(([pr, pcr]) => {
        setPlots((pr.data || []).slice(0, 3));
        setPc(pcr.data.data || {});
      })
      .catch(() => {});
  }, []);

  if (plots.length === 0) return null;

  const wa = contact?.whatsapp || "966555661937";

  return (
    <section id="plots" className="py-24 bg-[#0A1128] relative overflow-hidden">
      {/* Decorative */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-0 left-1/4 w-96 h-96 border border-[#ecc775]/5 rounded-full -translate-y-1/2" />
        <div className="absolute bottom-0 right-1/4 w-64 h-64 border border-[#ecc775]/5 translate-y-1/4" />
      </div>

      <div className="max-w-6xl mx-auto px-6 relative">
        {/* Header */}
        <div className="flex items-end justify-between mb-14 flex-wrap gap-6">
          <div>
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-0.5 bg-[#ecc775]" />
              <span className="text-[#ecc775] font-bold text-sm tracking-widest uppercase">{pc.preview_badge}</span>
            </div>
            <h2 className="text-white font-black text-4xl sm:text-5xl leading-tight">{pc.preview_title}</h2>
            <p className="text-white/55 mt-3 text-base max-w-lg leading-relaxed">{pc.preview_subtitle}</p>
          </div>
          <Link to="/plots" data-testid="plots-view-all"
            className="group hidden sm:flex items-center gap-2 text-[#ecc775] font-black hover:gap-4 transition-all duration-300 text-sm">
            {pc.preview_btn}
            <ArrowLeft size={16} className="group-hover:-translate-x-1 transition-transform" />
          </Link>
        </div>

        {/* Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {plots.map((plot, i) => {
            const img = fullUrl(plot.images?.[0]) || "https://images.unsplash.com/photo-1500382017468-9049fed747ef?w=800&q=80";
            const pdf = fullUrl(plot.pdf_url);
            const waMsg = encodeURIComponent(`مرحباً، أود الاستفسار عن ${plot.name}`);
            const scBg = STATUS_BG[plot.status] || "bg-green-500";

            return (
              <div key={plot.id || i}
                className="group bg-white/5 backdrop-blur-sm border border-white/10 rounded overflow-hidden hover:bg-white/8 hover:border-[#ecc775]/20 transition-all duration-500">
                {/* Image */}
                <div className="relative h-52 overflow-hidden">
                  <img src={img} alt={plot.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#0A1128] via-[#0A1128]/30 to-transparent" />
                  <span className={`absolute top-3 right-3 ${scBg} text-white text-xs font-black px-3 py-1 rounded-sm`}>
                    {plot.status || "متاح"}
                  </span>
                  {plot.badge && (
                    <span className="absolute top-3 left-3 bg-[#ecc775] text-[#173269] text-xs font-black px-3 py-1 rounded-sm">
                      {plot.badge}
                    </span>
                  )}
                </div>

                <div className="p-5">
                  <h3 className="text-white font-black text-lg mb-1 leading-tight">{plot.name}</h3>
                  {plot.location && (
                    <p className="text-white/55 text-sm flex items-center gap-1.5 mb-4">
                      <MapPin size={12} className="text-[#ecc775]" />{plot.location}
                    </p>
                  )}

                  {/* Stats — 2 cols (no available_plots) */}
                  <div className="flex gap-2 mb-5 flex-wrap">
                    {plot.total_plots && (
                      <div className="bg-white/8 rounded-sm px-3 py-2 text-center flex-1 min-w-0">
                        <p className="text-[#ecc775] font-black text-lg leading-none">{plot.total_plots}</p>
                        <p className="text-white/40 text-xs mt-0.5">{pc.total_plots_label}</p>
                      </div>
                    )}
                    {plot.plot_area && (
                      <div className="bg-white/8 rounded-sm px-3 py-2 text-center flex-1 min-w-0">
                        <p className="text-[#ecc775] font-black text-sm leading-none mt-0.5">{plot.plot_area}</p>
                        <p className="text-white/40 text-xs mt-0.5">{pc.area_label}</p>
                      </div>
                    )}
                    {plot.price_from && (
                      <div className="bg-white/8 rounded-sm px-3 py-2 text-center flex-1 min-w-0">
                        <p className="text-[#ecc775] font-black text-xs leading-none mt-1 truncate">{plot.price_from}</p>
                        <p className="text-white/40 text-xs mt-0.5">{pc.price_label}</p>
                      </div>
                    )}
                  </div>

                  {/* Buttons */}
                  <div className="flex gap-2">
                    <a href={`https://wa.me/${wa}?text=${waMsg}`} target="_blank" rel="noopener noreferrer"
                      className="flex-1 bg-[#ecc775] text-[#173269] text-xs font-black py-2.5 rounded-sm text-center hover:bg-white transition-colors flex items-center justify-center gap-1.5">
                      <Phone size={12} />{pc.contact_btn}
                    </a>
                    {pdf && (
                      <button onClick={() => openPdf(pdf, plot.name)}
                        className="flex-1 border border-[#ecc775]/30 text-[#ecc775] text-xs font-black py-2.5 rounded-sm text-center hover:border-[#ecc775]/70 hover:bg-[#ecc775]/5 transition-colors flex items-center justify-center gap-1.5">
                        <Eye size={12} />{pc.view_pdf_btn}
                      </button>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* View all */}
        <div className="text-center mt-12">
          <Link to="/plots"
            className="inline-flex items-center gap-3 bg-[#ecc775] text-[#173269] font-black px-10 py-4 rounded-sm hover:bg-white transition-all duration-300 text-base shadow-xl shadow-[#ecc775]/10">
            {pc.preview_btn}
            <ArrowLeft size={18} />
          </Link>
        </div>
      </div>

      {/* PDF Modal */}
      {pdfModal.open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm"
          onClick={e => { if (e.target === e.currentTarget) closePdf(); }}>
          <div className="bg-white rounded-sm shadow-2xl w-full max-w-4xl flex flex-col overflow-hidden" style={{ height: "90vh" }}>
            <div className="flex items-center justify-between px-5 py-3 bg-[#173269] flex-shrink-0">
              <div className="flex items-center gap-2">
                <Eye size={16} className="text-[#ecc775]" />
                <span className="text-white font-black text-sm">{pdfModal.title}</span>
              </div>
              <div className="flex items-center gap-2">
                <a href={pdfModal.url} download
                  className="text-[#ecc775]/70 hover:text-[#ecc775] text-xs font-bold transition-colors px-3 py-1.5 border border-[#ecc775]/20 rounded-sm hover:border-[#ecc775]/50">
                  تحميل
                </a>
                <button onClick={closePdf}
                  className="w-8 h-8 flex items-center justify-center text-white/60 hover:text-white hover:bg-white/10 rounded-sm transition-colors">
                  <X size={18} />
                </button>
              </div>
            </div>
            <iframe src={pdfModal.url} title={pdfModal.title} className="flex-1 w-full border-0" />
          </div>
        </div>
      )}
    </section>
  );
};

export default PlotsPreview;
